import React, { useState, useMemo, useCallback, useEffect } from "react";
import {
    Box,
    Card,
    CardContent,
    Typography,
    TextField,
    Button,
    IconButton,
    Divider,
    Stack,
    CircularProgress,
    useMediaQuery,
    useTheme,
    FormControl,
    Select,
    MenuItem,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/DeleteOutlined";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import SyncAltIcon from "@mui/icons-material/SyncAlt";
import StoreIcon from "@mui/icons-material/Store";
import PersonIcon from "@mui/icons-material/Person";
import DescriptionIcon from "@mui/icons-material/Description";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import { DATA_GRID_SX, DATA_GRID_LOCALE_TEXT } from "../../../../components/ui/dataGridStyles";
import { BOTON_PRIMARIO_SX, BOTON_SECUNDARIO_SX, ALTURA_BOTON } from "../../../../components/ui/buttonStyles";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

import ProductService from "../../../../services/product.service";
import { normalizarAlmacen, getStockEnAlmacen, getBarcode, getAllBarcodes, validarLineas } from "../../helpers/traspasoForm";
import { LineasTraspasoList } from "../lists";

// Columnas del DataGrid desktop
function buildColumnsDesktop({ onDeleteLinea }) {
    return [
        { field: "codigo", headerName: "Código", width: 130 },
        { field: "nombre", headerName: "Producto", flex: 1, minWidth: 250 },
        { field: "barras", headerName: "Barras", width: 140 },
        { field: "stockOrigen", headerName: "Stock origen", width: 130, type: "number" },
        { field: "stockDestino", headerName: "Stock destino", width: 130, type: "number" },
        {
            field: "unidades",
            headerName: "Unidades",
            width: 120,
            editable: true,
            type: "number",
        },
        {
            field: "acciones",
            headerName: "Acciones",
            width: 100,
            sortable: false,
            renderCell: (params) => (
                <IconButton onClick={() => onDeleteLinea(params.row.codigo)} size="small">
                    <DeleteIcon />
                </IconButton>
            ),
        },
    ];
}

/**
 * Formulario de creación/edición de traspasos
 */
export default function TraspasoForm({
    traspaso,
    almacenesOptions,
    empleadosOptions,
    onSave,
    onError,
    onCancel,
}) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
    const modoEdicion = Boolean(traspaso?.id);

    // Estado del formulario
    const [almacenOrigen, setAlmacenOrigen] = useState("");
    const [almacenDestino, setAlmacenDestino] = useState("");
    const [empleadoOrigen, setEmpleadoOrigen] = useState("");
    const [empleadoDestino, setEmpleadoDestino] = useState("");
    const [observaciones, setObservaciones] = useState("");

    // Estado búsqueda producto
    const [q, setQ] = useState("");
    const [opciones, setOpciones] = useState([]);
    const [loadingSearch, setLoadingSearch] = useState(false);
    const [productoSel, setProductoSel] = useState(null);
    const [unidades, setUnidades] = useState(1);

    // Estado líneas
    const [lineas, setLineas] = useState([]);
    const [guardando, setGuardando] = useState(false);

    // Cargar datos si editando
    useEffect(() => {
        if (traspaso) {
            setAlmacenOrigen(traspaso.almacenOrigen ?? "");
            setAlmacenDestino(traspaso.almacenDestino ?? "");
            setEmpleadoOrigen(traspaso.empleadoOrigenId ?? "");
            setEmpleadoDestino(traspaso.empleadoDestinoId ?? "");
            setObservaciones(traspaso.observaciones ?? "");

            if (traspaso.lineas) {
                setLineas(traspaso.lineas);
            }
        } else {
            // Resetear si creando nuevo
            setAlmacenOrigen("");
            setAlmacenDestino("");
            setEmpleadoOrigen("");
            setEmpleadoDestino("");
            setObservaciones("");
            setLineas([]);
        }
    }, [traspaso]);

    // Validaciones
    const validarCabecera = useCallback(() => {
        if (!almacenOrigen) return "Selecciona el almacén de origen.";
        if (!almacenDestino) return "Selecciona el almacén de destino.";
        if (String(almacenOrigen) === String(almacenDestino)) return "Origen y destino no pueden ser el mismo.";
        if (!empleadoOrigen) return "Selecciona el empleado de origen.";
        if (!empleadoDestino) return "Selecciona el empleado de destino.";
        return "";
    }, [almacenOrigen, almacenDestino, empleadoOrigen, empleadoDestino]);

    // Totales
    const totalUnidades = useMemo(
        () => lineas.reduce((acc, l) => acc + (Number(l.unidades) || 0), 0),
        [lineas]
    );

    // Búsqueda de productos
    const buscarProductos = useCallback(async (texto) => {
        if (!texto?.trim()) return;

        setLoadingSearch(true);
        try {
            const productos = await ProductService.getProducts(texto.trim());
            setOpciones(productos ?? []);

            // Auto-seleccionar si hay un solo resultado
            if (productos && productos.length === 1) {
                setProductoSel(productos[0]);
            }
        } catch (err) {
            console.error("Error buscando productos:", err);
            setOpciones([]);
        } finally {
            setLoadingSearch(false);
        }
    }, []);

    useEffect(() => {
        if (q && q.length >= 3) {
            const timer = setTimeout(() => buscarProductos(q), 300);
            return () => clearTimeout(timer);
        } else if (!productoSel) {
            setOpciones([]);
        }
    }, [q, buscarProductos, productoSel]);

    // Añadir línea
    const onAddLinea = useCallback(() => {
        if (!productoSel) return;
        if (!almacenOrigen || !almacenDestino) {
            const mensaje = "Selecciona almacenes de origen y destino primero.";
            if (onError) onError(mensaje);
            return;
        }

        const codigo = productoSel.CODIGO;
        const nombre = productoSel.NOMBRE ?? "";
        const barras = getBarcode(productoSel);
        const stockOrigen = getStockEnAlmacen(productoSel, almacenOrigen);
        const stockDestino = getStockEnAlmacen(productoSel, almacenDestino);

        const u = Number(unidades);
        if (!Number.isFinite(u) || u <= 0) {
            const mensaje = "Unidades inválidas.";
            if (onError) onError(mensaje);
            return;
        }

        const lineaExistente = lineas.find((l) => l.codigo === codigo);
        if (lineaExistente) {
            // Actualizar línea existente sumando unidades
            const nuevasUnidades = lineaExistente.unidades + u;

            if (stockOrigen != null && nuevasUnidades > stockOrigen) {
                const mensaje = `No hay suficiente stock en origen. Stock disponible: ${stockOrigen}, solicitado: ${nuevasUnidades}`;
                if (onError) onError(mensaje);
                return;
            }

            setLineas((prev) =>
                prev.map((l) =>
                    l.codigo === codigo ? { ...l, unidades: nuevasUnidades } : l
                )
            );
        } else {
            // Añadir nueva línea
            if (stockOrigen != null && u > stockOrigen) {
                const mensaje = `No hay suficiente stock en origen. Stock disponible: ${stockOrigen}`;
                if (onError) onError(mensaje);
                return;
            }

            setLineas((prev) => [...prev, { id: codigo, codigo, nombre, barras, stockOrigen, stockDestino, unidades: u }]);
        }
        setProductoSel(null);
        setQ("");
        setUnidades(1);
    }, [productoSel, almacenOrigen, almacenDestino, unidades, lineas, onError]);

    // Eliminar línea
    const onDeleteLinea = useCallback((codigo) => {
        setLineas((prev) => prev.filter((l) => l.codigo !== codigo));
    }, []);

    // Actualizar unidades (input libre)
    const onUpdateUnidades = useCallback((codigo, raw) => {
        setLineas((prev) =>
            prev.map((l) => (l.codigo === codigo ? { ...l, unidadesInput: raw } : l))
        );
    }, []);

    // Confirmar unidades (al salir del input)
    const onCommitUnidades = useCallback((codigo) => {
        setLineas((prev) =>
            prev.map((l) => {
                if (l.codigo !== codigo) return l;

                const u = Number(l.unidadesInput ?? l.unidades ?? 0);
                if (!Number.isFinite(u) || u <= 0) {
                    const mensaje = `Línea ${codigo}: unidades inválidas.`;
                    if (onError) onError(mensaje);
                    return { ...l, unidadesInput: String(l.unidades ?? 1) };
                }

                if (l.stockOrigen != null && u > l.stockOrigen) {
                    const mensaje = `Línea ${codigo}: supera stock de origen (${l.stockOrigen}).`;
                    if (onError) onError(mensaje);
                    return { ...l, unidadesInput: String(l.unidades ?? 1) };
                }

                        return { ...l, unidades: u, unidadesInput: undefined };
            })
        );
    }, [onError]);

    // Edición de celda en DataGrid
    const onProcessRowUpdate = useCallback((newRow) => {
        const u = Number(newRow.unidades);
        if (!Number.isFinite(u) || u <= 0) throw new Error("Unidades inválidas.");
        if (newRow.stockOrigen != null && u > newRow.stockOrigen) throw new Error("Supera stock de origen.");

        setLineas((prev) => prev.map((l) => (l.codigo === newRow.codigo ? { ...l, unidades: u } : l)));
        return { ...newRow, unidades: u };
    }, []);

    // Guardar traspaso
    const handleGuardar = useCallback(async () => {
        // Validar cabecera
        const errorCab = validarCabecera();
        if (errorCab) {
            if (onError) onError(errorCab);
            return;
        }

        // Validar líneas
        const { ok, errorMsg, lineasNormalizadas } = validarLineas(lineas);
        if (!ok) {
            if (onError) onError(errorMsg);
            return;
        }

        setGuardando(true);
        try {
            const payload = {
                almacenOrigen: normalizarAlmacen(almacenOrigen),
                almacenDestino: normalizarAlmacen(almacenDestino),
                empleadoOrigenId: empleadoOrigen,
                empleadoDestinoId: empleadoDestino,
                observaciones: observaciones.trim(),
                lineas: lineasNormalizadas.map((l) => ({ codigoArticulo: l.codigo, unidades: Number(l.unidades) })),
            };

            if (onSave) {
                await onSave(payload, modoEdicion ? 'updated' : 'created');
            }

            // Resetear formulario
            setAlmacenOrigen("");
            setAlmacenDestino("");
            setEmpleadoOrigen("");
            setEmpleadoDestino("");
            setObservaciones("");
            setLineas([]);
            } catch (err) {
            if (onError) {
                onError(err.response?.data?.message || err.message || "Error al guardar");
            }
        } finally {
            setGuardando(false);
        }
    }, [validarCabecera, lineas, almacenOrigen, almacenDestino, empleadoOrigen, empleadoDestino, observaciones, modoEdicion, onSave, onError]);

    const handleCancelar = () => {
        if (onCancel) {
            onCancel();
        }
        // Resetear formulario
        setAlmacenOrigen("");
        setAlmacenDestino("");
        setEmpleadoOrigen("");
        setEmpleadoDestino("");
        setObservaciones("");
        setLineas([]);
    };

    const columnsDesktop = useMemo(() => buildColumnsDesktop({ onDeleteLinea }), [onDeleteLinea]);

    const filterOptions = useMemo(
        () =>
            createFilterOptions({
                stringify: (opt) => {
                    const barcodes = getAllBarcodes(opt).join(" ");
                    return `${opt?.CODIGO ?? ""} ${opt?.NOMBRE ?? ""} ${barcodes}`;
                },
            }),
        []
    );

    return (
        <Card sx={{ boxShadow: "0 2px 8px rgba(0,0,0,0.1)", borderRadius: 2 }}>
            <CardContent sx={{ p: 3 }}>
                {/* Header */}
                <Box sx={{ mb: 3 }}>
                    <Typography
                        variant={isMobile ? "h6" : "h5"}
                        sx={{
                            fontWeight: 700,
                            color: "#212529",
                            mb: 0.5,
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                        }}
                    >
                        <SyncAltIcon sx={{ fontSize: isMobile ? 24 : 28 }} />
                        {modoEdicion ? `Editar traspaso #${traspaso.id}` : "Nuevo traspaso"}
                    </Typography>
                    <Typography variant="body2" sx={{ color: "#6c757d" }}>
                        {modoEdicion
                            ? "Modifica los datos del traspaso"
                            : "Crea un nuevo traspaso entre almacenes"}
                    </Typography>
                </Box>

                {/* CABECERA */}
                <Stack spacing={3}>
                    {/* Almacenes */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <StoreIcon sx={{ fontSize: 18 }} />
                                Almacén origen *
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={almacenOrigen}
                                    onChange={(e) => setAlmacenOrigen(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        Selecciona almacén origen
                                    </MenuItem>
                                    {almacenesOptions.map((alm) => (
                                        <MenuItem key={alm.value} value={alm.value}>
                                            {alm.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <StoreIcon sx={{ fontSize: 18 }} />
                                Almacén destino *
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={almacenDestino}
                                    onChange={(e) => setAlmacenDestino(e.target.value)}
                                    displayEmpty
                                    disabled={!almacenOrigen}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        Selecciona almacén destino
                                    </MenuItem>
                                    {almacenesOptions
                                        .filter((alm) => alm.value !== almacenOrigen)
                                        .map((alm) => (
                                            <MenuItem key={alm.value} value={alm.value}>
                                                {alm.label}
                                            </MenuItem>
                                        ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>

                    {/* Empleados */}
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 18 }} />
                                Empleado origen *
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={empleadoOrigen}
                                    onChange={(e) => setEmpleadoOrigen(e.target.value)}
                                    displayEmpty
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        Selecciona empleado origen
                                    </MenuItem>
                                    {empleadosOptions.map((emp) => (
                                        <MenuItem key={emp.value} value={emp.value}>
                                            {emp.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>

                        <Box sx={{ flex: 1 }}>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontWeight: 600,
                                    mb: 1,
                                    color: "#495057",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 0.5,
                                }}
                            >
                                <PersonIcon sx={{ fontSize: 18 }} />
                                Empleado destino *
                            </Typography>
                            <FormControl fullWidth>
                                <Select
                                    value={empleadoDestino}
                                    onChange={(e) => setEmpleadoDestino(e.target.value)}
                                    displayEmpty
                                    disabled={!empleadoOrigen}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            backgroundColor: "#fff",
                                        },
                                    }}
                                >
                                    <MenuItem value="">
                                        Selecciona empleado destino
                                    </MenuItem>
                                    {empleadosOptions.map((emp) => (
                                        <MenuItem key={emp.value} value={emp.value}>
                                            {emp.label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Stack>

                    {/* Observaciones */}
                    <Box>
                        <Typography
                            variant="body2"
                            sx={{
                                fontWeight: 600,
                                mb: 1,
                                color: "#495057",
                                display: "flex",
                                alignItems: "center",
                                gap: 0.5,
                            }}
                        >
                            <DescriptionIcon sx={{ fontSize: 18 }} />
                            Observaciones
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={3}
                            value={observaciones}
                            onChange={(e) => setObservaciones(e.target.value)}
                            placeholder="Comentarios adicionales..."
                            sx={{
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fff",
                                },
                            }}
                        />
                    </Box>
                </Stack>

                {/* BÚSQUEDA PRODUCTO */}
                <Box sx={{ mt: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: "#495057",
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                        }}
                    >
                        <SearchIcon sx={{ fontSize: 18 }} />
                        Añadir producto
                    </Typography>

                    <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                        <Box sx={{ flex: 1 }}>
                            <Autocomplete
                                value={productoSel}
                                onChange={(_, newValue) => setProductoSel(newValue)}
                                inputValue={q}
                                onInputChange={(_, newInputValue) => setQ(newInputValue)}
                                options={opciones}
                                loading={loadingSearch}
                                getOptionLabel={(opt) => `${opt.CODIGO} - ${opt.NOMBRE ?? ""}`}
                                filterOptions={filterOptions}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Buscar por código, nombre o barras..."
                                        InputProps={{
                                            ...params.InputProps,
                                            endAdornment: (
                                                <>
                                                    {loadingSearch ? <CircularProgress color="inherit" size={20} /> : null}
                                                    {params.InputProps.endAdornment}
                                                </>
                                            ),
                                        }}
                                    />
                                )}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "#fff",
                                    },
                                }}
                            />
                        </Box>

                        <TextField
                            type="number"
                            label="Unidades"
                            value={unidades}
                            onChange={(e) => setUnidades(e.target.value)}
                            inputProps={{ min: 1 }}
                            sx={{
                                width: { xs: "100%", sm: 140 },
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#fff",
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={onAddLinea}
                            sx={{ ...BOTON_PRIMARIO_SX, height: ALTURA_BOTON.FILTRO }}
                        >
                            Añadir
                        </Button>
                    </Stack>
                </Box>

                {/* LÍNEAS */}
                <Box sx={{ mt: 3 }}>
                    <Typography
                        variant="body2"
                        sx={{
                            fontWeight: 600,
                            mb: 1,
                            color: "#495057",
                        }}
                    >
                        Líneas del traspaso
                    </Typography>

                    {!isMobile ? (
                        <DataGrid
                            rows={lineas}
                            columns={columnsDesktop}
                            autoHeight
                            disableRowSelectionOnClick
                            hideFooter
                            processRowUpdate={onProcessRowUpdate}
                            onProcessRowUpdateError={(error) => {
                                if (onError) onError(error.message);
                            }}
                            density="standard"
                            rowHeight={52}
                            columnHeaderHeight={56}
                            sx={{
                                ...DATA_GRID_SX,
                                "& .MuiDataGrid-cell": {
                                    ...DATA_GRID_SX["& .MuiDataGrid-cell"],
                                    py: 1,
                                },
                                "& .MuiDataGrid-cell:focus, & .MuiDataGrid-columnHeader:focus": {
                                    outline: "none",
                                },
                            }}
                            localeText={DATA_GRID_LOCALE_TEXT}
                        />
                    ) : (
                        <LineasTraspasoList
                            lineas={lineas}
                            onUpdateUnidades={onUpdateUnidades}
                            onCommitUnidades={onCommitUnidades}
                            onDeleteLinea={onDeleteLinea}
                        />
                    )}
                </Box>

                <Divider sx={{ my: 3 }} />

                {/* RESUMEN Y ACCIONES */}
                <Stack spacing={2}>
                    <Typography variant="body1" sx={{ textAlign: { xs: "center", sm: "right" }, color: "#6c757d" }}>
                        Líneas: <b>{lineas.length}</b> · Total unidades: <b>{totalUnidades}</b>
                    </Typography>

                    <Stack direction={{ xs: "column-reverse", sm: "row" }} spacing={2}>
                        <Button
                            variant="contained"
                            onClick={handleGuardar}
                            disabled={guardando}
                            fullWidth={isMobile}
                            startIcon={guardando ? <CircularProgress size={18} sx={{ color: "white" }} /> : <SaveIcon />}
                            sx={{ ...BOTON_PRIMARIO_SX, minWidth: { sm: 220 } }}
                        >
                            {guardando
                                ? "Guardando..."
                                : modoEdicion
                                ? "Actualizar traspaso"
                                : "Generar traspaso"}
                        </Button>
                        <Button
                            variant="outlined"
                            onClick={handleCancelar}
                            disabled={guardando}
                            fullWidth={isMobile}
                            sx={BOTON_SECUNDARIO_SX}
                        >
                            Cancelar
                        </Button>
                    </Stack>
                </Stack>
            </CardContent>
        </Card>
    );
}
